// Shared date-range presets for report filters (This Month, Last Quarter, …).
// All math is calendar-based and timezone-safe (no Date parsing of ISO strings).

export type PresetKey =
  | 'this_month'
  | 'last_month'
  | 'this_quarter'
  | 'last_quarter'
  | 'this_year'
  | 'last_year'
  | 'all'
  | 'custom';

export interface DateRange {
  from: string;
  to: string;
}

export const PRESETS: { key: PresetKey; label: string }[] = [
  { key: 'this_month', label: 'This Month' },
  { key: 'last_month', label: 'Last Month' },
  { key: 'this_quarter', label: 'This Quarter' },
  { key: 'last_quarter', label: 'Last Quarter' },
  { key: 'this_year', label: 'This Year' },
  { key: 'last_year', label: 'Last Year' },
  { key: 'all', label: 'All Dates' },
  { key: 'custom', label: 'Custom' },
];

// The "beginning of time" for All Dates. Opening balances predating this are not expected.
export const ALL_DATES_FROM = '1900-01-01';

function ymd(y: number, m: number, d: number): string {
  return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

// Last calendar day of month m (1-based) in year y. Day 0 of the next month rolls back.
function lastDay(y: number, m: number): number {
  return new Date(y, m, 0).getDate();
}

function todayStr(today: Date): string {
  return ymd(today.getFullYear(), today.getMonth() + 1, today.getDate());
}

/** The {from,to} a preset resolves to relative to `today`. Returns null for 'custom'. */
export function rangeForPreset(key: PresetKey, today: Date = new Date()): DateRange | null {
  const y = today.getFullYear();
  const m = today.getMonth() + 1; // 1-based

  switch (key) {
    case 'this_month':
      return { from: ymd(y, m, 1), to: ymd(y, m, lastDay(y, m)) };
    case 'last_month': {
      const lm = m === 1 ? 12 : m - 1;
      const ly = m === 1 ? y - 1 : y;
      return { from: ymd(ly, lm, 1), to: ymd(ly, lm, lastDay(ly, lm)) };
    }
    case 'this_quarter': {
      const startM = Math.floor((m - 1) / 3) * 3 + 1;
      const endM = startM + 2;
      return { from: ymd(y, startM, 1), to: ymd(y, endM, lastDay(y, endM)) };
    }
    case 'last_quarter': {
      let startM = Math.floor((m - 1) / 3) * 3 + 1 - 3;
      let yy = y;
      if (startM <= 0) {
        startM += 12;
        yy -= 1;
      }
      const endM = startM + 2;
      return { from: ymd(yy, startM, 1), to: ymd(yy, endM, lastDay(yy, endM)) };
    }
    case 'this_year':
      return { from: ymd(y, 1, 1), to: ymd(y, 12, 31) };
    case 'last_year':
      return { from: ymd(y - 1, 1, 1), to: ymd(y - 1, 12, 31) };
    case 'all':
      return { from: ALL_DATES_FROM, to: todayStr(today) };
    case 'custom':
      return null;
  }
}

/** Which preset (if any) a from/to pair matches; 'custom' when none do. */
export function detectRangePreset(from: string, to: string, today: Date = new Date()): PresetKey {
  for (const { key } of PRESETS) {
    if (key === 'custom') continue;
    const r = rangeForPreset(key, today);
    if (r && r.from === from && r.to === to) return key;
  }
  return 'custom';
}

/**
 * The as-of (single) date a preset implies for cumulative reports (Balance Sheet, aging).
 * Uses the period end, but never beyond today — a balance sheet "as of" a future date is
 * meaningless, so the current month/quarter/year and All Dates all resolve to today.
 */
export function asOfForPreset(key: PresetKey, today: Date = new Date()): string | null {
  const r = rangeForPreset(key, today);
  if (!r) return null;
  const t = todayStr(today);
  return r.to < t ? r.to : t;
}

/** Which preset an as-of date matches; prefers All Dates for "today", else a past period. */
export function detectAsOfPreset(asOf: string, today: Date = new Date()): PresetKey {
  if (asOf === todayStr(today)) return 'all';
  for (const { key } of PRESETS) {
    if (key === 'custom' || key === 'all') continue;
    if (asOfForPreset(key, today) === asOf) return key;
  }
  return 'custom';
}
